import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useT } from '../i18n';
import { useAuth } from '../providers/AuthProvider';
import { useToast } from '../providers/ToastProvider';

/**
 * Venues a signed-in person has hearted, kept on their account so the list
 * follows them to another device.
 *
 * Saving needs an account: a visitor who taps a heart is sent to sign in and
 * brought back to the page they were on. Signed out, the list is simply empty.
 * The key carries the user id, so one person's list never shows for the next
 * person to sign in on the same browser.
 */
const EMPTY: number[] = [];
const favoriteKeys = { mine: (userId: number) => ['favorites', userId] as const };

export function useFavorites() {
  const { user } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const key = favoriteKeys.mine(user?.id ?? 0);

  const query = useQuery({
    queryKey: key,
    queryFn: () => api<{ ids: number[] }>('/favorites'),
    select: (d) => d.ids,
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const ids = user ? query.data ?? EMPTY : EMPTY;

  // Optimistic: the heart fills on the tap, and goes back if the server says no.
  const mutation = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      api<{ ok: true }>(`/favorites/${id}`, { method: on ? 'PUT' : 'DELETE' }),
    onMutate: async ({ id, on }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ ids: number[] }>(key);
      const cur = prev?.ids ?? [];
      qc.setQueryData(key, { ids: on ? [id, ...cur.filter((x) => x !== id)] : cur.filter((x) => x !== id) });
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      qc.setQueryData(key, ctx?.prev ?? { ids: [] });
      toast(err instanceof Error ? err.message : t('favSaveFailed'), 'bad');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  /** Flip a venue. Returns false when nothing changed because nobody is signed in. */
  const toggle = useCallback(
    (id: number) => {
      if (!user) {
        toast(t('favLoginToSave'));
        navigate('/login', { state: { from: location.pathname + location.search } });
        return false;
      }
      mutation.mutate({ id, on: !ids.includes(id) });
      return true;
    },
    [user, ids, mutation, navigate, location.pathname, location.search, toast, t],
  );
  const has = useCallback((id: number) => ids.includes(id), [ids]);
  return useMemo(() => ({ ids, has, toggle, signedIn: Boolean(user) }), [ids, has, toggle, user]);
}
